import _ from 'lodash';
import React from 'react';
import { Button, Form } from 'react-bootstrap';

import Checkbox from 'components/Checkbox';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import RadioButtonList from 'components/ModalWindows/Shared/RadioButtonList';
import { PdfPageOrder, isVeevaMedcomms, ProjectType, UploadProjectType } from 'const';
import { UploadContextOptions } from 'models';
import { getBinder } from 'services/api';
import { intlGet } from 'utils/intlGet';
import { BinderFormState } from '../ManageBinder/binderFormReducer';
import { ProjectTypeSettings, UploadProjectProps, UploadProjectState } from './models';
import styles from './styles.module.scss';

const UploadProjectTypeSettings: Record<UploadProjectType, ProjectTypeSettings> = {
  [UploadProjectType.DRAFT]: {
    text: () => intlGet('UploadProjectWindow.Option', 'ReadyForReview'),
    completeTranslationDisabled: true,
  },
  [UploadProjectType.READY_FOR_REVIEW]: {
    text: () => intlGet('UploadProjectWindow.Option', 'Draft'),
    completeTranslationDisabled: false,
  },
};

const UploadButton: React.FC<{
  uploadProject: (binderForm?: BinderFormState | null) => React.MouseEventHandler;
}> = ({ uploadProject }) => {
  const buttonPrefix = 'UploadProjectWindow.Button';

  return (
    <Button
      onClick={uploadProject()}
      variant="primary"
    >
      {intlGet(buttonPrefix, 'Upload')}
    </Button>
  );
};

class UploadProject extends React.PureComponent<UploadProjectProps, UploadProjectState> {
  readonly state: UploadProjectState = {
    selectedUploadProjectType: UploadProjectType.DRAFT,
    translationComplete: false,
    isBinderAlreadyCreated: false,
    loadingGetLinkedBinders: false,
    pdfPageOrder: PdfPageOrder.MOBILE_FIRST,
  };

  private readonly toggleTranslationComplete = (): void => {
    this.setState(prevState => ({
      translationComplete: !prevState.translationComplete,
    }));
  };

  private readonly onUploadProjectTypeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedUploadProjectType = event.target.name as UploadProjectType;
    const { completeTranslationDisabled } = UploadProjectTypeSettings[selectedUploadProjectType];

    this.setState(prevState => ({
      selectedUploadProjectType,
      translationComplete: !completeTranslationDisabled && prevState.translationComplete,
    }));
  };

  private readonly uploadProject = (binderFormState?: BinderFormState): React.MouseEventHandler => () => {
    const { hideModal, uploadToPromoMats } = this.props;
    const { selectedUploadProjectType, translationComplete, pdfPageOrder } = this.state;

    uploadToPromoMats(selectedUploadProjectType, translationComplete, binderFormState, pdfPageOrder);
    hideModal();
  };

  private readonly getLabel = (uploadProjectType: UploadProjectType): JSX.Element => {
    const { text } = UploadProjectTypeSettings[uploadProjectType];

    return (
      <div className={styles.formCheckLabel}>{text()}</div>
    );
  };

  private readonly onPdfPageOrderChange = (pdfPageOrder: PdfPageOrder): void => {
    this.setState({ pdfPageOrder });
  };

  private readonly isPdfPageOrderDisabled = (): boolean =>
    this.state.selectedUploadProjectType === UploadProjectType.DRAFT;

  private readonly getPdfPageOrderLabel = (pdfPageOrder: PdfPageOrder): JSX.Element => {
    const labels: Record<PdfPageOrder, string> = {
      [PdfPageOrder.MOBILE_FIRST]: intlGet('PdfPageOrder', 'MobileFirst'),
      [PdfPageOrder.DESKTOP_FIRST]: intlGet('PdfPageOrder', 'DesktopFirst'),
    };

    return <div className={styles.formCheckLabel}>{labels[pdfPageOrder]}</div>;
  };

  private readonly handleManageBinderModalOpen = (): void => {
    const { createBinderDuplicate, hideModal, showBinderModal } = this.props;
    const { isBinderAlreadyCreated, selectedUploadProjectType, translationComplete, pdfPageOrder } = this.state;

    const uploadContext: UploadContextOptions = {
      uploadProjectType: selectedUploadProjectType,
      translationComplete,
      pdfPageOrder,
    };

    if (isBinderAlreadyCreated) {
      createBinderDuplicate(uploadContext);
    } else {
      hideModal();
      showBinderModal({
        disableClickOutside: true,
        uploadContext,
      });
    }
  };

  checkIsBinderAlreadyCreated = async (): Promise<void> => {
    this.setState({
      loadingGetLinkedBinders: true,
    });

    try {
      const linkedBinders = await getBinder(this.props.rootDocumentId);

      this.setState({
        isBinderAlreadyCreated: linkedBinders.length > 0,
        loadingGetLinkedBinders: false,
      });
    } catch (error) {
      this.setState({
        isBinderAlreadyCreated: false,
        loadingGetLinkedBinders: false,
      });
    }
  };

  componentDidMount(): void {
    const { projectType } = this.props;
    const isBinderCreationEnabled = projectType === ProjectType.IPAD && !isVeevaMedcomms();
    if (isBinderCreationEnabled) {
      void this.checkIsBinderAlreadyCreated();
    }
  }

  render(): React.ReactNode {
    const { selectedUploadProjectType, translationComplete, pdfPageOrder } = this.state;
    const { hideModal, isProjectTranslatable, projectType, hasBothMobileAndDesktopViews } = this.props;
    const buttonPrefix = 'UploadProjectWindow.Button';
    const isBinderCreationEnabled = projectType === ProjectType.IPAD && !isVeevaMedcomms();
    const showPdfPageOrder = hasBothMobileAndDesktopViews && !isVeevaMedcomms();

    return (
      <div className={styles.UploadProject}>
        <header>{intlGet('UploadProjectWindow', 'UploadToPromoMats')}</header>
        <section>
          <Form>
            <Form.Group>
              <Form.Label className={styles.formLabel}>{intlGet('UploadProjectWindow', 'SelectOption')}</Form.Label>
              {
                _.map(UploadProjectTypeSettings, (text, type: UploadProjectType) => {
                  return (
                    <Form.Check
                      type="radio"
                      key={type}
                      id={type}
                      label={this.getLabel(type)}
                      name={type}
                      checked={type === selectedUploadProjectType}
                      onChange={this.onUploadProjectTypeChange}
                      className={styles.formCheck}
                    />
                  );
                })
              }
            </Form.Group>
            {isProjectTranslatable && <Checkbox
              className={styles.checkbox}
              onChange={this.toggleTranslationComplete}
              checked={translationComplete}
              disabled={UploadProjectTypeSettings[selectedUploadProjectType].completeTranslationDisabled}
              label={intlGet('UploadProjectWindow.Translation', 'Complete')}
              comments={intlGet('UploadProjectWindow.Translation', 'Comments')}
            />}
          </Form>
        </section>
        {showPdfPageOrder && (
          <section>
            <RadioButtonList<PdfPageOrder>
              id="upload-project-pdf-order"
              options={[PdfPageOrder.MOBILE_FIRST, PdfPageOrder.DESKTOP_FIRST]}
              listLabel={intlGet('PdfPageOrder', 'SelectPdfPageOrder')}
              onOptionChange={this.onPdfPageOrderChange}
              isDisabled={this.isPdfPageOrderDisabled}
              activeOption={pdfPageOrder}
              getOptionLabel={this.getPdfPageOrderLabel}
            />
          </section>
        )}
        <footer>
          <div className={styles.mainButtons}>
            <Button onClick={hideModal} variant="secondary">{intlGet(buttonPrefix, 'Cancel')}</Button>
            <UploadButton uploadProject={this.uploadProject} />
          </div>
          {
            isBinderCreationEnabled ?
              <Button
                onClick={this.handleManageBinderModalOpen}
                variant='secondary'
                className={styles.manageBinder}
                disabled={this.state.loadingGetLinkedBinders}
              >
                <Icon type={IconType.NEW_FILE} size='sm' color='inherit' />
                {intlGet(buttonPrefix, 'UploadAndCreateBinder')}
              </Button> : null
          }
        </footer>
      </div>
    );
  }
}

export default UploadProject;
